<template>
  <div class="generatorBackdrop" @click.self="close">
    <section
      class="generator"
      role="dialog"
      aria-modal="true"
      aria-labelledby="generator-title"
    >
      <header class="generatorHeader">
        <h2 id="generator-title">Generator liczby</h2>
        <button
          ref="closeButton"
          type="button"
          class="closeButton"
          aria-label="Zamknij generator liczby"
          @click="close"
        >
          ×
        </button>
      </header>

      <form class="generatorForm" @submit.prevent="generate">
        <label for="generated-number">Liczba</label>
        <input
          id="generated-number"
          :value="number"
          type="text"
          inputmode="numeric"
          autocomplete="off"
          placeholder="np. 63249017"
          aria-describedby="generator-help"
          @input="formatNumberInput"
        />
        <p id="generator-help">
          Wpisz liczbę o długości podzielnej przez 4 cyfry.
        </p>
        <p v-if="error" class="generatorError" role="alert">{{ error }}</p>
        <button type="submit">Generuj</button>
      </form>

      <div v-if="groups.length" class="generatorResults">
        <article
          v-for="(group, index) in groups"
          :key="`${group.value}-${index}`"
          class="generatorGroup"
        >
          <p class="generatorCode">{{ group.value }}</p>
          <div class="generatorAssociations">
            <img
              :src="'./img/' + group.firstDigit + '000.jpg'"
              :alt="`Skojarzenie dla cyfry ${group.firstDigit}`"
            />
            <div
              class="generatorColor"
              :style="{ backgroundColor: group.color.backgroundColor }"
              :aria-label="`Kolor dla cyfry ${group.secondDigit}`"
            ></div>
            <img
              :src="'./img/' + group.lastTwoDigits + '.jpg'"
              :alt="`Skojarzenie dla liczby ${group.lastTwoDigits}`"
            />
          </div>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
import { COLORS } from '../data/colors';

export default {
  emits: ['close'],
  data() {
    return {
      number: '',
      submittedNumber: '',
      error: '',
    };
  },
  computed: {
    groups() {
      if (!this.submittedNumber) {
        return [];
      }

      return this.submittedNumber.match(/.{4}/g).map((value) => ({
        value,
        firstDigit: value[0],
        secondDigit: value[1],
        lastTwoDigits: value.slice(2),
        color: COLORS[Number(value[1])],
      }));
    },
  },
  methods: {
    formatNumberInput(event) {
      const digits = event.target.value.replace(/\D/g, '');
      this.number = digits.replace(/(.{4})/g, '$1 ').trim();
    },
    generate() {
      const digits = this.number.replace(/\s/g, '');

      if (!/^\d+$/.test(digits)) {
        this.error = 'Wpisz wyłącznie cyfry.';
        this.submittedNumber = '';
        return;
      }

      if (digits.length % 4 !== 0) {
        this.error = 'Liczba musi mieć długość podzielną przez 4.';
        this.submittedNumber = '';
        return;
      }

      this.error = '';
      this.submittedNumber = digits;
    },
    close() {
      this.$emit('close');
    },
    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
      }
    },
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown);
    this.$nextTick(() => this.$refs.closeButton.focus());
  },
  unmounted() {
    window.removeEventListener('keydown', this.handleKeydown);
  },
};
</script>

<style scoped>
.generatorBackdrop {
  position: fixed;
  z-index: 101;
  inset: 0;
  overflow-y: auto;
  padding: 16px;
  background: rgb(0 0 0 / 70%);
}

.generator {
  width: min(100%, 520px);
  margin: 0 auto;
  border-radius: 20px;
  color: #e8e8e8;
  background: #1a1a1a;
  box-shadow: 0 20px 60px rgb(0 0 0 / 60%);
}

.generatorHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
}

.generatorHeader h2 {
  margin: 0;
  font-size: 1.1rem;
}

.closeButton {
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 50%;
  color: #fff;
  background: transparent;
  font-size: 2rem;
  line-height: 1;
  cursor: pointer;
}

.closeButton:hover,
.closeButton:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
  background: rgb(255 255 255 / 15%);
}

.generatorForm {
  display: grid;
  gap: 8px;
  padding: 4px 16px 20px;
}

.generatorForm input {
  width: 100%;
  min-height: 44px;
  border: 1px solid #777;
  border-radius: 8px;
  padding: 8px 12px;
  color: #fff;
  background: #0a0a0a;
  font: inherit;
}

.generatorForm input:focus-visible,
.generatorForm button:focus-visible {
  outline: 3px solid #fff;
  outline-offset: 2px;
}

.generatorForm p {
  margin: 0;
  color: #aaa;
  font-size: 0.85rem;
}

.generatorForm .generatorError {
  color: #ff9c9c;
}

.generatorForm button {
  justify-self: start;
  min-height: 44px;
  border: 0;
  border-radius: 8px;
  padding: 8px 16px;
  color: #fff;
  background: #2f9e44;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.generatorResults {
  display: grid;
  gap: 16px;
  padding: 0 16px 24px;
}

.generatorGroup {
  margin: 0;
}

.generatorCode {
  margin: 0 0 8px;
  color: #aaa;
  font-size: 0.9rem;
  letter-spacing: 0.12em;
  text-align: center;
}

.generatorAssociations {
  --squareSize: clamp(76px, 27vw, 115px);
  display: grid;
  grid-template-columns: repeat(3, var(--squareSize));
  width: fit-content;
  margin: 0 auto;
  overflow: hidden;
  border-radius: 16px;
}

.generatorAssociations img,
.generatorColor {
  width: var(--squareSize);
  height: var(--squareSize);
}

.generatorAssociations img {
  display: block;
  object-fit: cover;
}
</style>
